import { useState } from 'react'

import {
  useBulkDeleteRecordings,
  useDeleteRecording,
  useDiskStats,
  useRecordings,
  streamUrl,
  type RecordingItem,
} from '../../api'
import { AudioPlayer } from '../../components/AudioPlayer'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useNotify } from '../../components/Notifications'
import { Pagination } from '../../components/Pagination'
import { RecordingActionsMenu } from '../../components/RecordingActionsMenu'
import { RecordingStatusBadge } from '../../components/RecordingStatusBadge'
import { HelpButton, StatusesHelpModal } from '../../components/StatusesHelpModal'
import { formatDateTime } from '../../lib/format'

const PAGE_SIZE = 50

function formatBytes(n: number | undefined): string {
  if (!n || n <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let u = 0
  let v = n
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024
    u++
  }
  return `${v.toFixed(1)} ${units[u]}`
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function DiskStatsWidget() {
  const { data, isLoading, error } = useDiskStats()

  if (isLoading) {
    return (
      <div className="text-sm text-gray-400 dark:text-gray-500" data-testid="disk-stats-loading">
        Загрузка статистики диска…
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="text-sm text-red-500" data-testid="disk-stats-error">
        Не удалось получить статистику диска.
      </div>
    )
  }
  if (!data.enabled) {
    return (
      <div
        className="text-sm text-amber-600 dark:text-amber-400"
        data-testid="disk-stats-disabled"
      >
        Запись звонков выключена (RELAY_INTERNAL_URL не настроен).
      </div>
    )
  }

  const usedPct = data.total_bytes > 0
    ? Math.round(((data.total_bytes - data.free_bytes) / data.total_bytes) * 100)
    : 0

  const barColor = usedPct > 90 ? 'bg-red-500' : usedPct > 75 ? 'bg-amber-500' : 'bg-emerald-500'

  return (
    <div className="flex items-center gap-3 text-sm" data-testid="disk-stats">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <span className="text-gray-600 dark:text-gray-400">
            Записи: <strong>{formatBytes(data.recordings_bytes)}</strong>
          </span>
          <span className="text-gray-500 dark:text-gray-500">
            Свободно: {formatBytes(data.free_bytes)} / {formatBytes(data.total_bytes)}
          </span>
        </div>
        <div className="mt-1 h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded overflow-hidden">
          <div
            className={`h-full ${barColor}`}
            style={{ width: `${usedPct}%` }}
            aria-label={`Использовано ${usedPct}% диска`}
          />
        </div>
      </div>
      {data.skip_new_recordings && (
        <span
          className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400"
          data-testid="skip-new-badge"
        >
          Skip new
        </span>
      )}
    </div>
  )
}

function RecordingRow({
  rec,
  onDelete,
  checked,
  onCheckedChange,
}: {
  rec: RecordingItem
  onDelete: (r: RecordingItem) => void
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  // incomplete = canonical playable, late fragment lost — file still streams.
  const playable =
    rec.status === 'ready' ||
    rec.status === 'partial_sim' ||
    rec.status === 'partial_op' ||
    rec.status === 'incomplete'

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <td className="py-2 px-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onCheckedChange(e.target.checked)}
          aria-label={`Select ${rec.call_id}`}
          data-testid={`select-${rec.call_id}`}
        />
      </td>
      <td className="py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {rec.recorded_at ? formatDateTime(rec.recorded_at) : formatDateTime(rec.created_at)}
      </td>
      <td className="py-2 text-sm font-mono text-gray-700 dark:text-gray-300">
        {rec.call_id}
      </td>
      <td className="py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {formatDuration(rec.duration_seconds)}
      </td>
      <td className="py-2">
        <RecordingStatusBadge status={rec.status} />
      </td>
      <td className="py-2">
        {playable ? (
          <AudioPlayer src={streamUrl(rec.call_id)} testId={`audio-${rec.call_id}`} controlsTestId={rec.call_id} />
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        )}
      </td>
      <td className="py-2 text-right">
        <div className="inline-flex justify-end">
          <RecordingActionsMenu
            callId={rec.call_id}
            streamUrl={playable ? streamUrl(rec.call_id) : null}
            onRequestDelete={() => onDelete(rec)}
            triggerTestId={`menu-${rec.call_id}`}
            downloadTestId={`download-${rec.call_id}`}
            deleteTestId={`delete-${rec.call_id}`}
          />
        </div>
      </td>
    </tr>
  )
}

export function RecordingsPage() {
  const [page, setPage] = useState(1)
  const [pendingDelete, setPendingDelete] = useState<RecordingItem | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const { data, isLoading, error } = useRecordings({ page, pageSize: PAGE_SIZE })
  const deleteMutation = useDeleteRecording()
  const bulkDeleteMutation = useBulkDeleteRecordings()
  const notify = useNotify()

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1

  const confirmDelete = () => {
    if (!pendingDelete) return
    deleteMutation.mutate(pendingDelete.call_id, {
      onSuccess: () => {
        notify('Запись удалена', 'info')
        setPendingDelete(null)
      },
      onError: err => notify(err.message || 'Ошибка удаления', 'error'),
    })
  }

  const toggleSelected = (callId: string, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (checked) next.add(callId)
      else next.delete(callId)
      return next
    })
  }

  const selectAllOnPage = (checked: boolean) => {
    if (!data) return
    setSelected(prev => {
      const next = new Set(prev)
      for (const item of data.items) {
        if (checked) next.add(item.call_id)
        else next.delete(item.call_id)
      }
      return next
    })
  }

  const confirmBulkDelete = () => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    bulkDeleteMutation.mutate(ids, {
      onSuccess: res => {
        const failed = res.results.filter(r => r.status === 'error')
        if (failed.length > 0) {
          notify(
            `Частично удалено: ${res.results.length - failed.length}/${res.results.length}. ` +
              `Ошибки: ${failed.map(f => f.call_id).join(', ')}`,
            'warning',
          )
        } else {
          notify(`Удалено ${res.results.length}`, 'info')
        }
        setSelected(new Set())
        setPendingBulkDelete(false)
      },
      onError: err => notify(err.message || 'Ошибка удаления', 'error'),
    })
  }

  const allOnPageSelected =
    data && data.items.length > 0 && data.items.every(i => selected.has(i.call_id))

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Записи звонков</h2>
            <HelpButton onClick={() => setHelpOpen(true)} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {data
              ? `${data.total} ${data.total === 1 ? 'запись' : 'записей'}`
              : ' '}
          </p>
        </div>
      </div>

      <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <DiskStatsWidget />
      </div>

      {isLoading && (
        <div className="text-sm text-gray-500 dark:text-gray-400">Загрузка…</div>
      )}
      {error && (
        <div className="text-sm text-red-600">
          Ошибка: {(error as Error).message}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Записей пока нет.
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          {selected.size > 0 && (
            <div
              className="flex items-center justify-between mb-3 p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900"
              data-testid="bulk-bar"
            >
              <span className="text-sm text-blue-700 dark:text-blue-300">
                Выбрано: <strong>{selected.size}</strong>
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs px-3 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40"
                  data-testid="bulk-clear"
                >
                  Сбросить
                </button>
                <button
                  type="button"
                  onClick={() => setPendingBulkDelete(true)}
                  className="text-xs font-medium px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                  data-testid="bulk-delete-btn"
                >
                  Удалить выбранные
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="py-2 px-2">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={e => selectAllOnPage(e.target.checked)}
                      aria-label="Select all on page"
                      data-testid="select-all"
                    />
                  </th>
                  <th className="py-2 px-2">Время</th>
                  <th className="py-2 px-2">Call ID</th>
                  <th className="py-2 px-2">Длительность</th>
                  <th className="py-2 px-2">Статус</th>
                  <th className="py-2 px-2">Плеер</th>
                  <th className="py-2 px-2 text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(rec => (
                  <RecordingRow
                    key={rec.id}
                    rec={rec}
                    onDelete={setPendingDelete}
                    checked={selected.has(rec.call_id)}
                    onCheckedChange={c => toggleSelected(rec.call_id, c)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Удалить запись?"
        message={
          pendingDelete
            ? `Запись звонка ${pendingDelete.call_id} будет удалена с диска без возможности восстановления.`
            : ''
        }
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingBulkDelete}
        title={`Удалить ${selected.size} ${selected.size === 1 ? 'запись' : 'записей'}?`}
        message={`Выбранные записи будут удалены с диска без возможности восстановления.`}
        confirmLabel="Удалить"
        isPending={bulkDeleteMutation.isPending}
        onConfirm={confirmBulkDelete}
        onClose={() => setPendingBulkDelete(false)}
      />

      <StatusesHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}
